import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBookOpen, FiUsers, FiTrendingUp, FiShield, FiTarget, FiExternalLink, FiAward, FiBarChart } = FiIcons;

function Research() {
  const researchAreas = [
    {
      icon: FiShield,
      title: "Psykologisk trygghet",
      description: "Grunden för högpresterande team",
      color: "from-blue-500 to-blue-600",
      studies: [
        {
          title: "The Fearless Organization",
          author: "Amy Edmondson, Harvard Business School",
          year: "2019",
          finding: "Team med hög psykologisk trygghet presterar 19% bättre och har 27% mindre personalomsättning.",
          link: "#"
        },
        {
          title: "Google's Project Aristotle",
          author: "Google People Operations",
          year: "2016",
          finding: "Psykologisk trygghet var den enskilt viktigaste faktorn för framgångsrika team.",
          link: "#"
        }
      ]
    },
    {
      icon: FiUsers,
      title: "Tillit på arbetsplatsen",
      description: "Byggsten för effektiva organisationer",
      color: "from-green-500 to-green-600",
      studies: [
        {
          title: "The Trust Factor",
          author: "Paul Zak, Claremont Graduate University",
          year: "2017",
          finding: "Anställda i högtillitsorganisationer upplever 74% mindre stress och 50% högre produktivitet.",
          link: "#"
        },
        {
          title: "Trust and Team Performance",
          author: "MIT Sloan Management Review",
          year: "2020",
          finding: "Team med hög tillit är 2.5 gånger mer benägna att vara högpresterande.",
          link: "#"
        }
      ]
    },
    {
      icon: FiTrendingUp,
      title: "Innovation och kreativitet",
      description: "Hur mänskliga faktorer driver innovation",
      color: "from-purple-500 to-purple-600",
      studies: [
        {
          title: "Psychological Safety and Innovation",
          author: "Journal of Business Research",
          year: "2021",
          finding: "Psykologisk trygghet ökar innovativ problemlösning med 67% och kreativitet med 42%.",
          link: "#"
        },
        {
          title: "The Innovation Imperative",
          author: "McKinsey Institute",
          year: "2022",
          finding: "Organisationer med inkluderande kulturer är 3.5 gånger mer innovativa.",
          link: "#"
        }
      ]
    },
    {
      icon: FiTarget,
      title: "Välmående och prestation",
      description: "Sambandet mellan mänskligt välmående och resultat",
      color: "from-orange-500 to-orange-600",
      studies: [
        {
          title: "Workplace Wellbeing Index",
          author: "Gallup Organization",
          year: "2023",
          finding: "Engagerade medarbetare är 23% mer lönsamma och har 18% högre produktivitet.",
          link: "#"
        },
        {
          title: "The Happy-Productive Worker",
          author: "Oxford Saïd Business School",
          year: "2019",
          finding: "Lyckliga medarbetare är 13% mer produktiva och stannar 31% längre på jobbet.",
          link: "#"
        }
      ]
    }
  ];

  const keyFindings = [
    {
      statistic: "76%",
      description: "av medarbetare i psykologiskt trygga miljöer rapporterar högre engagemang",
      source: "MIT Sloan, 2022"
    },
    {
      statistic: "47%",
      description: "minskning av sjukfrånvaro när medarbetare kan vara autentiska på jobbet",
      source: "Harvard Business Review, 2021"
    },
    {
      statistic: "3.2x",
      description: "högre sannolikhet för innovation i team som uppmuntrar 'misslyckanden'",
      source: "Stanford Research Institute, 2020"
    },
    {
      statistic: "58%",
      description: "av medarbetare säger att de presterar bättre när de kan vara sig själva",
      source: "Deloitte Global Survey, 2023"
    }
  ];

  const theoreticalFoundations = [
    {
      theory: "Maslows behovshierarki",
      application: "Säkerhet och tillhörighet som grundbehov på arbetsplatsen",
      relevance: "Okej-kulturen adresserar de grundläggande behoven av säkerhet och social tillhörighet som måste uppfyllas innan självförverkligande kan ske."
    },
    {
      theory: "Självbestämmandeteorin (SDT)",
      application: "Autonomi, kompetens och relatedness som drivkrafter",
      relevance: "De fyra principerna i Okej-kulturen stödjer alla tre grundläggande psykologiska behoven som driver motivation och välmående."
    },
    {
      theory: "Social Identity Theory",
      application: "Gruppidentitet och tillhörighet påverkar prestation",
      relevance: "Genom att skapa en kultur där det är 'okej att vara människa' stärks den positiva gruppidentiteten och sammanhållningen."
    },
    {
      theory: "Broaden-and-Build Theory",
      application: "Positiva känslor utvidgar tänkande och bygger resurser",
      relevance: "En trygg och accepterande miljö genererar positiva känslor som leder till bredare tänkande och starkare team-resurser."
    }
  ];

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">Forskning</span> & Bakgrund
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Vetenskapliga studier och forskningsresultat som ligger till grund för 
              Okej-kulturen och dess principer för den moderna, humana arbetsplatsen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nyckelstatistik från forskningen
            </h2>
            <p className="text-lg text-gray-600">
              Siffror som visar kraften i en human arbetsplatskultur
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyFindings.map((finding, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg text-center"
              >
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600 mb-2">
                  {finding.statistic}
                </div>
                
                <p className="text-gray-700 mb-3 leading-relaxed">
                  {finding.description}
                </p>
                
                <p className="text-xs text-gray-500">
                  {finding.source}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Forskningsområden
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Djupdykningar i de vetenskapliga områden som stödjer Okej-kulturens principer
            </p>
          </motion.div>

          <div className="space-y-12">
            {researchAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 shadow-lg"
              >
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${area.color} flex items-center justify-center mr-4`}>
                    <SafeIcon icon={area.icon} className="text-white text-2xl" />
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {area.title}
                    </h3>
                    <p className="text-gray-600">
                      {area.description}
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {area.studies.map((study, studyIndex) => (
                    <div key={studyIndex} className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-bold text-gray-900 flex-1">
                          {study.title}
                        </h4>
                        <SafeIcon icon={FiExternalLink} className="text-gray-400 ml-2 flex-shrink-0" />
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        {study.author} • {study.year}
                      </p>
                      
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Resultat:</strong> {study.finding}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Theoretical Foundations */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Teoretiska grunder
            </h2>
            <p className="text-lg text-gray-600">
              Etablerade psykologiska och organisatoriska teorier som stödjer Okej-kulturen
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {theoreticalFoundations.map((foundation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mr-3">
                    <SafeIcon icon={FiAward} className="text-white text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {foundation.theory}
                  </h3>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Tillämpning:</p>
                    <p className="text-sm text-gray-600">{foundation.application}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Relevans för Okej-kulturen:</p>
                    <p className="text-sm text-gray-600">{foundation.relevance}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Evidence */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-green-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Evidensbaserad implementering
            </h2>
            
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Okej-kulturen bygger på decennier av forskning inom organisationspsykologi, 
              ledarskap och teamdynamik. Varje princip har vetenskapligt stöd.
            </p>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <SafeIcon icon={FiBookOpen} className="text-white text-3xl mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">200+</h3>
                  <p className="text-blue-100 text-sm">Vetenskapliga studier granskade</p>
                </div>
                
                <div>
                  <SafeIcon icon={FiUsers} className="text-white text-3xl mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">50+</h3>
                  <p className="text-blue-100 text-sm">Organisationer studerade</p>
                </div>
                
                <div>
                  <SafeIcon icon={FiBarChart} className="text-white text-3xl mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">15+</h3>
                  <p className="text-blue-100 text-sm">År av forskningsdata</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Research;